import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api, ApiError,
  type ImportBatch, type ImportPreview, type ImportProblem, type ImportResult,
  type ImportUndone,
} from '../lib/api'

/** Uploading a phone book — `PLAN.html` sections 06 and 19.
 *
 *  Three steps in one dialog: pick the file, confirm which column is which,
 *  then look at what would happen and press the button.
 *
 *  THE MAPPING STEP IS THE POINT, NOT A FORMALITY. The file comes from an
 *  administrator's own spreadsheet, so its headings are that person's
 *  shorthand — "Tel2", "Fam", "Addr" — and no list of guesses is ever
 *  complete. The server suggests; a human confirms. The columns are listed
 *  down the page AS THEY APPEAR IN THE FILE, with two real values from each,
 *  because that is what lets somebody recognise their own column without
 *  knowing any of our field names.
 *
 *  AND THE PREVIEW IS MANDATORY. Every dangerous thing in this product works
 *  this way — `migrate` and `backfill_phones` without `--apply`, every Test
 *  button in the console. A column mapped wrongly across two thousand rows is
 *  not something anybody repairs by hand.
 */

const IGNORE = '—'

/** What each outcome means, in the words somebody would use about it. The
 *  server counts by grouping, so anything not named here still renders — with
 *  its raw key, which is ugly and visible, rather than being dropped. */
const OUTCOME_LABEL: Record<string, string> = {
  create: 'new contacts',
  widen: 'already here — blanks filled in',
  number_held: 'number belongs to somebody else — left alone',
  number_unreadable: 'number could not be read — contact kept without it',
  no_number: 'no phone number',
  no_name: 'a number with no name',
  duplicate_in_file: 'the same number twice in this file',
}

/** Only used if a server somehow answers without `writes`. The real list
 *  comes down with the preview, so this cannot drift from the importer. */
const FALLBACK_WRITES = ['create', 'widen', 'number_unreadable', 'no_number', 'no_name']

export function ImportContacts({
  onClose, onImported,
}: {
  onClose: () => void
  onImported: (message: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [hasHeaders, setHasHeaders] = useState(true)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [batches, setBatches] = useState<ImportBatch[]>([])
  const [undone, setUndone] = useState<ImportUndone | null>(null)
  const downOnBackdrop = useRef(false)

  /** The recent imports, so one can be taken back later and not only in the
   *  minute after it ran. */
  const loadBatches = useCallback(async () => {
    try {
      setBatches(await api.contactImportBatches())
    } catch {
      // A missing history is not worth an error over the import itself.
      setBatches([])
    }
  }, [])
  useEffect(() => { loadBatches() }, [loadBatches])

  const dirty = file !== null && result === null

  function leave() {
    if (busy) return
    if (dirty && !confirm('Close without importing? Nothing has been written yet.')) return
    onClose()
  }

  /** Hand the browser the example file. Same shape as the reports export. */
  async function getTemplate() {
    setError(null)
    try {
      const blob = await api.contactImportTemplate()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'phone-book-template.csv'
      a.click()
      // Released on the next tick: revoking it synchronously races the click
      // in some browsers and the download arrives empty.
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not fetch the template.')
    }
  }

  async function look(chosen: File, map?: Record<string, number>, headers?: boolean) {
    setBusy(true)
    setError(null)
    try {
      const p = await api.contactImportPreview(chosen, map, headers)
      setPreview(p)
      setMapping(map ?? p.mapping)
      setHasHeaders(headers ?? p.has_headers)
    } catch (e) {
      setPreview(null)
      setError(e instanceof ApiError ? e.message : 'Could not read that file.')
    } finally {
      setBusy(false)
    }
  }

  function pick(chosen: File | null) {
    setFile(chosen)
    setPreview(null)
    setResult(null)
    setError(null)
    if (chosen) look(chosen)
  }

  /** Change one column's target and re-read the file through the new mapping.
   *  Re-read on the SERVER rather than re-interpreted here: the preview has to
   *  be the same code as the apply, or it is a preview of something else. */
  function retarget(columnIndex: number, target: string) {
    if (!file || !preview) return
    const next: Record<string, number> = {}
    for (const [k, v] of Object.entries(mapping)) {
      if (v !== columnIndex && k !== target) next[k] = v
    }
    if (target !== IGNORE) next[target] = columnIndex
    setMapping(next)
    look(file, next, hasHeaders)
  }

  function targetFor(columnIndex: number): string {
    const found = Object.entries(mapping).find(([, v]) => v === columnIndex)
    return found ? found[0] : IGNORE
  }

  async function run() {
    if (!file || !preview) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.contactImportApply(
        file, mapping, preview.file_sha256, hasHeaders)
      setResult(r)
      loadBatches()
      onImported(
        `${r.contacts_created} added, ${r.contacts_widened} already here and `
        + `filled in, ${r.numbers_attached} numbers attached.`,
      )
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The import did not run.')
    } finally {
      setBusy(false)
    }
  }

  /** Take a whole import back.
   *
   *  THE COST IS NAMED BEFORE THE BUTTON DOES ANYTHING, and it is fetched
   *  fresh rather than remembered: what an undo destroys depends on what has
   *  happened since, and "since" keeps growing while the dialog is open. The
   *  one genuine loss is a number a dispatcher typed onto an imported record
   *  afterwards, because `contact_phones` goes with the contact.
   */
  async function undo(batch: ImportBatch) {
    setBusy(true)
    setError(null)
    try {
      const fresh = await api.contactImportBatch(batch.id)
      const cost = fresh.undo
      const lines = [
        `Reverse the import of ${fresh.filename ?? 'that file'}?`,
        '',
        `${fresh.contacts_still_present} contacts it created will be deleted.`,
      ]
      if (cost?.widened) {
        lines.push(
          `${cost.widened} records it filled in will go back to how they were,`
          + ' except anything somebody has changed since.')
      }
      if (cost?.calls || cost?.incidents) {
        lines.push(
          `${(cost.calls ?? 0) + (cost.incidents ?? 0)} calls it attached will`
          + ' go back to showing an unknown number. Nothing is deleted.')
      }
      if (cost?.hand_added_numbers) {
        lines.push('', `WARNING: ${cost.hand_added_numbers} phone numbers have`
          + ' been added by hand to these records since the import. Those will'
          + ' be lost.')
      }
      if (cost?.edited_since) {
        lines.push(`${cost.edited_since} of these records have been edited`
          + ' since the import.')
      }
      if (!confirm(lines.join('\n'))) return
      const done = await api.contactImportUndo(batch.id)
      setUndone(done)
      setResult(null)
      loadBatches()
      onImported(
        `Import reversed: ${done.contacts_deleted} contacts removed`
        + (done.left_alone
          ? `, ${done.left_alone} values left alone because they had been changed since.`
          : '.'),
      )
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That import was not reversed.')
    } finally {
      setBusy(false)
    }
  }

  const counts = result?.counts ?? preview?.counts ?? {}
  const problems = result?.problems ?? preview?.problems ?? []
  // FROM THE SERVER, not added up here. Summing `create` and `widen` in the
  // browser is a second definition of "will this write anything", and it was
  // already wrong: a row whose number could not be read still creates the
  // person, and that outcome is in neither of those two counters.
  const willWrite = preview?.will_write ?? 0

  return (
    <div
      className="modalback"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) leave()
      }}
    >
      <div
        className="modal wide sheet-rec import"
        role="dialog"
        aria-modal="true"
        aria-labelledby="imptitle"
      >
        <header className="sheet-rechead">
          <h3 id="imptitle">Import a phone book</h3>
          {busy && <span className="sheet-recstate">Working…</span>}
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        <div className="sheet-recbody">
          {error && <div className="notice" role="alert">{error}</div>}

          {/* ---- step one: the file ---- */}
          <div className="field">
            <label htmlFor="imp-file">A .csv or .xlsx file</label>
            <input
              id="imp-file"
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy}
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
            />
          </div>

          {/* THE TEMPLATE BELONGS HERE, next to the thing it explains. The
              person who needs it is the one standing at the upload button
              about to go and ask somebody for a list, and a template that
              lives anywhere else is a template nobody finds.

              A plain link, not a fetch-and-Blob like the reports export: there
              is nothing to post, nothing sensitive to keep out of a URL, and
              the browser's own download is one less thing to get wrong. The
              route is gated the same as everything else on this screen. */}
          <p className="footnote">
            Not sure what to send?{' '}
            <button className="linky" type="button" onClick={getTemplate}>
              Download a template
            </button>{' '}
            — fill it in, or forward it to whoever keeps the list. The columns
            can be renamed or moved; you will confirm what each one is after
            you upload.
          </p>
          <p className="footnote">
            Nothing is written until you press Import. An older <code>.xls</code>{' '}
            cannot be read — open it in Excel and use Save As to make a{' '}
            <code>.xlsx</code>.{' '}
            <strong>Format the phone column as Text in Excel</strong> — a number
            column loses a leading zero before the file ever gets here, and no
            software can tell afterwards that it was there. A number from
            outside {preview?.region ?? 'this country'} needs its <code>+</code>{' '}
            and country code.
          </p>

          {preview && !result && (
            <>
              {/* ---- step two: which column is which ---- */}
              <h4 className="sheet-recsub">Which column is which</h4>
              <label className="sheet-colopt" style={{ padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={hasHeaders}
                  disabled={busy}
                  onChange={(e) => {
                    setHasHeaders(e.target.checked)
                    if (file) look(file, undefined, e.target.checked)
                  }}
                />
                <span>The first row is headings, not a person</span>
              </label>

              <table className="maptable">
                <thead>
                  <tr>
                    <th>Column in your file</th>
                    <th>First values</th>
                    <th>Import as</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map((header, i) => (
                    <tr key={i}>
                      <td className="nm">{header || <span className="muted">(no heading)</span>}</td>
                      {/* Two real values from the column. Recognising your own
                          data is what makes a heading like "Tel2" legible. */}
                      <td className="muted clip">
                        {preview.sample
                          .slice(0, 2)
                          .map((s) => (i === mapping.display_name ? s.display_name
                            : i === mapping.phone ? s.phone : ''))
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                      <td>
                        <select
                          value={targetFor(i)}
                          disabled={busy}
                          onChange={(e) => retarget(i, e.target.value)}
                        >
                          <option value={IGNORE}>{IGNORE} do not import</option>
                          {Object.entries(preview.targets).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 className="sheet-recsub">What would happen</h4>
              <p className="footnote">
                {preview.rows_read.toLocaleString()} rows
                {preview.blank_rows_excluded > 0
                  && ` (${preview.blank_rows_excluded.toLocaleString()} blank rows ignored)`}
                {preview.region && `, read as ${preview.region} numbers`}.
              </p>
            </>
          )}

          {(preview || result) && (
            <Counts counts={counts} writes={preview?.writes} />
          )}

          {result && (
            <p className="footnote">
              {result.contacts_created.toLocaleString()} contacts added,{' '}
              {result.contacts_widened.toLocaleString()} already here and filled
              in, {result.numbers_attached.toLocaleString()} numbers attached.
              {result.stopped_at_row !== null && (
                <>
                  {' '}<strong>It stopped at row {result.stopped_at_row}.</strong>{' '}
                  Everything before that is imported and stays imported — fix
                  the file from that row and import the rest.
                </>
              )}
            </p>
          )}

          {undone && (
            <div className="notice ok" role="status">
              Reversed. {undone.contacts_deleted} contacts removed,{' '}
              {undone.numbers_removed} numbers, {undone.contacts_restored}{' '}
              records put back.{' '}
              {undone.calls_unclaimed + undone.incidents_unclaimed > 0 && (
                <>
                  {undone.calls_unclaimed + undone.incidents_unclaimed} calls
                  show an unknown number again — none were deleted.{' '}
                </>
              )}
              {undone.left_alone > 0 && (
                <>
                  {undone.left_alone} values were left as they are, because
                  somebody changed them after the import.
                </>
              )}
            </div>
          )}

          {problems.length > 0 && (
            <>
              <h4 className="sheet-recsub">Rows worth looking at</h4>
              <Problems problems={problems} />
            </>
          )}

          {!preview && !result && batches.length > 0 && (
            <>
              <h4 className="sheet-recsub">Recent imports</h4>
              <p className="footnote">
                A whole import can be taken back. Reversing deletes the contacts
                it created and puts back the records it filled in — except
                anything somebody has changed since, which is left alone.
              </p>
              <table className="maptable">
                <thead>
                  <tr><th>File</th><th>When</th><th>Contacts</th><th /></tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id}>
                      <td className="nm clip">{b.filename ?? '—'}</td>
                      <td className="muted">
                        {new Date(b.created_at).toLocaleString([], {
                          dateStyle: 'short', timeStyle: 'short',
                        })}
                      </td>
                      <td className="num">{b.contacts_still_present}</td>
                      <td>
                        {b.undone_at ? (
                          <span className="src">reversed</span>
                        ) : (
                          <button
                            className="ghost tiny nogrow danger"
                            type="button"
                            disabled={busy}
                            onClick={() => undo(b)}
                          >
                            Reverse
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="modalrow">
          <button className="ghost" type="button" onClick={leave} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {/* Right where somebody looks the moment they see the numbers are
              wrong — which is when a bulk import is nearly always caught. */}
          {result && !undone && (
            <button
              className="ghost danger"
              type="button"
              disabled={busy}
              onClick={() => undo({
                id: result.batch_id,
                filename: null,
                row_count: result.rows_read,
                imported_count: result.contacts_created,
                skipped_count: null,
                created_at: new Date().toISOString(),
                imported_by_name: null,
                undone_at: null,
                undo_kept: null,
                contacts_still_present: result.contacts_created,
              })}
            >
              Reverse this import
            </button>
          )}
          {!result && (
            <button
              className="primary compact"
              type="button"
              disabled={busy || !preview || willWrite === 0}
              onClick={run}
            >
              {busy ? 'Working…' : `Import ${willWrite.toLocaleString()} contacts`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/** Every outcome the server reported, including one this screen has no label
 *  for. A fixed list of named counters is how a broadcast that reached
 *  everybody rendered no outcome at all. */
function Counts({
  counts, writes,
}: {
  counts: Record<string, number>
  writes?: string[]
}) {
  const good = new Set(writes ?? FALLBACK_WRITES)
  const entries = Object.entries(counts)
    .filter(([key, n]) => n > 0 && key !== 'blank')
    .sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return <div className="dim">Nothing to import.</div>
  return (
    <div className="chips">
      {entries.map(([key, n]) => (
        <span className={good.has(key) ? 'chip on' : 'chip'} key={key}>
          {n.toLocaleString()} {OUTCOME_LABEL[key] ?? key}
        </span>
      ))}
    </div>
  )
}

/** THE ROW AND THE COLUMN AND WHY. Never the value — the person reading this
 *  has the file open in front of them, and the number would only be a second
 *  copy of somebody's phone number on a screen. */
function Problems({ problems }: { problems: ImportProblem[] }) {
  return (
    <table className="maptable">
      <thead>
        <tr><th>Row</th><th>Column</th><th>What happened</th></tr>
      </thead>
      <tbody>
        {problems.map((p) => (
          <tr key={`${p.row}-${p.outcome}`}>
            <td className="num">{p.row}</td>
            <td className="muted">{p.column ?? '—'}</td>
            <td>
              {p.held_by
                ? `That number is already on ${p.held_by}, so this row was left alone.`
                : p.first_seen_row
                  ? `The same number is on row ${p.first_seen_row} of this file.`
                  : p.reason ?? (OUTCOME_LABEL[p.outcome] ?? p.outcome)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
